<template>
  <ScrollArea
    ref="blocklistScrollRef"
    class="scroll-null"
    @bottom="nextPage" >
    <div class="flex column settings-css manage">
      <div
        v-if="!checkPermission('manage_settings') || forbidden" >
        <h1 class="settings__title">
          Blocked contacts
        </h1>
        <Forbidden />
      </div>

      <div
        v-else
        class="settings--limit-width">
        <h1 class="settings__title">
          Blocked contacts
        </h1>

        <Spinner
          v-if="!initialBlockedRequestFinished"
          class="mt-5"
          :size="20"
          :thickness="2" />

        <InfoCard
          v-if="!searchModeActive && !fetchingBlocked && initialBlockedRequestFinished && business.id && !blocklist.data.length && !searchQuery"
          :flat-icon="SBIconList.BLOCKED_CONTACTS"
          title="Blocked contacts"
          :description="noBlockedDescription"
          :action="false" />

        <!-- Actions bar -->
        <div
          v-if="initialBlockedRequestFinished && (blocklist.data.length || searchQuery || searchModeActive)"
          class="sticky-top" >
          <!-- Search section -->
          <div class="flex space-between settings--box bottom-20 top-35">
            <SearchInput
              ref="searchInput"
              v-model="searchQuery"
              placeholder="Search contacts..." />
          </div>
        </div>

        <!-- No data info -->
        <div
          v-if="initialBlockedRequestFinished && !fetchingBlocked && !filteredBlocked.length && (searchQuery || searchModeActive)"
          class="no-data flex center-all">
          <span class="wrapper flex center-all">
            <span class="emoji">😐</span>
            <p>We couldn’t find anything for <strong>{{ searchQuery }}</strong></p>
          </span>
        </div>

        <!-- Data grid -->
        <div
          v-if="initialBlockedRequestFinished && filteredBlocked.length && !fetchingBlocked"
          ref="tableGrid"
          class="table__grid clickable bottom-40">
          <span class="column-name sticky-top sticky-second">Name</span>
          <span class="column-name sticky-top sticky-second">Number</span>
          <span class="column-name sticky-top sticky-second" />

          <span
            v-if="blocklist.meta.total"
            class="full-width table-count">CONTACTS ({{ meta.total }})</span>

          <template
            v-for="(contact, index) in blocklist.data" >
            <!-- Name column cell -->
            <div
              :key="`${index}-status`"
              class="grid-item"
              :class="[`hover-${index}`, 'flex', 'flex-align-center', {hide: !filteredBlockedIds.includes(contact.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(contact)" >
              <SBAvatar
                :show-client="true"
                :person="contact"
                :size="UserDropdownButtonSize.S" />
              <p
                class="ellipsis"
                :class="{muted: !contact.name}"
                :inner-html.prop="(!contact.name ? 'No name' : contact.name) | boldMatch(searchQuery)" />
            </div>

            <!-- Number column cell -->
            <div
              :key="`${index}-url`"
              class="grid-item"
              :class="[`hover-${index} flex flex-align-center`, {hide: !filteredBlockedIds.includes(contact.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(contact)" >
              <p
                class="ellipsis"
                :inner-html.prop="contact.phone | phoneFormat | boldMatch(searchQuery)" />
            </div>

            <!-- Menu column cell -->
            <div
              :key="`${index}-action`"
              class="grid-item gi-button"
              :class="[`hover-${index} flex flex-align-center`, {hide: !filteredBlockedIds.includes(contact.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(contact)" >
              <b-button
                class="flex center-all"
                @click="unblock(contact)">
                <span class="flex center-all">Unblock</span>
              </b-button>
            </div>
          </template>
        </div>
        <Spinner
          v-if="initialBlockedRequestFinished && fetchingBlocked"
          style="height: 65px;"
          class="my-3 full-width spinner-border-top"
          :size="20"
          :thickness="2" />
      </div>
    </div>
  </ScrollArea>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import escapeStringRegexp from 'escape-string-regexp';
import debounce from 'debounce';

import ToasterMixin from '@/mixins/Toaster';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SearchInput from '@/components/business/widgets/SearchInput';
import Spinner from '@/components/common/Spinner';
import ScrollArea from '@/components/common/ScrollArea';
import InfoCard from '@/components/common/InfoCard';
import Forbidden from '@/components/errors/Forbidden';
import SBAvatar from '@/components/common/Avatar';
import UserDropdownButtonSize from '@/helpers/lists/userDropdownButtonSize';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';

const noBlockedDescription = 'If you don’t want to receive messages from a certain phone number, you can block it. Your blocked numbers will appear here.';

export default {
  name: 'BlocklistSettings',
  components: {
    Spinner,
    SearchInput,
    ScrollArea,
    SBIcon,
    Forbidden,
    InfoCard,
    SBAvatar,
  },
  mixins: [
    ToasterMixin,
    UserPermissionsMixin,
  ],
  data() {
    return {
      UserDropdownButtonSize,
      initialBlockedRequestFinished: false,
      searchModeActive: false,
      searchQuery: '',
      forbidden: false,
      SBColors,
      SBIconList,
      noBlockedDescription,
      pageLimit: 50,
      meta: null,
    };
  },
  computed: {
    ...mapGetters({
      fetchingBlocked: 'merchant/fetchingBlocked',
      blocklist: 'merchant/blocklist',
      conversationActivity: 'pusher/conversationActivity',
      apiError: 'merchant/apiError',
      userPermissions: 'user/userPermissions',
      currentOrganisation: 'organisation/currentOrganisation',
    }),
    business() {
      return this.currentOrganisation.businesses.find(b => b.id === this.$route.params.businessId);
    },
    // TODO v3 filter the data immediatly on input change. Then the additional search will be triggered after a debounce but only if not all pages are loaded.
    filteredBlocked() {
      return this.blocklist.data;
      // return this.blocklist.data.filter(contact => (RegExp(`(${escapeStringRegexp(this.searchQuery)})`, 'i').test(contact.name) || RegExp(`(${escapeStringRegexp(this.searchQuery)})`).test(contact.phone)));
    },
    filteredBlockedIds() {
      return this.blocklist.data.map(number => number.id);
      // return this.filteredBlocked.map(number => number.id);
    },
  },
  watch: {
    blocklist() {
      if (this.blocklist.meta) {
        this.initialBlockedRequestFinished = true;
        this.meta = { ...this.blocklist.meta }; // NOTE - total results in paging is acurate only on the first page so this is why we are only using it from initial response
        setTimeout(() => {
          this.loadMoreIfNeeded();
        }, 0);
      }
      if (!this.searchQuery) {
        this.searchModeActive = false;
      }
    },
    business() {
      if (this.checkPermission('manage_settings') && this.business.id) {
        this.CLEAR_BLOCKED_CONTACTS();
        this.initialBlockedRequestFinished = false;
        this.fetchBlockedContacts({ businessId: this.business.id, data: { limit: this.pageLimit } });
      }
    },
    searchQuery() {
      this.searchDebounce();
    },
    conversationActivity(payload) {
      if (payload.business_uid === this.business.id) {
        if (payload.activity.action === 'Block') {
          // Get contact details
          this.fetchContact({
            merchant_id: this.business.id,
            contact_id: payload.activity.contact_id,
          })
            .then((response) => {
              if (response.status === 200) {
                this.ADD_BLOCKER_CONTACT_TO_LIST({
                  conversation_id: payload.activity.conversation_id,
                  id: payload.activity.contact_id,
                  name: response.data.name,
                  phone: response.data.phone,
                });
              }
            });
        } else if (payload.activity.action === 'Unblock') {
          const matchedContact = this.blocklist.data.find(contact => contact.id === payload.activity.contact_id);
          if (matchedContact) {
            this.REMOVE_BLOCKER_CONTACT_FROM_LIST(matchedContact.id);
          }
        }
      }
    },
    userPermissions() {
      if (this.checkPermission('manage_settings') && this.business.id) {
        this.CLEAR_BLOCKED_CONTACTS();
        this.initialBlockedRequestFinished = false;
        this.fetchBlockedContacts({ businessId: this.business.id, data: { limit: this.pageLimit } });
      }
    },
  },
  mounted() {
    if (this.checkPermission('manage_settings') && this.business.id) {
      this.fetchBlockedContacts({ businessId: this.business.id, data: { limit: this.pageLimit } });
    }
    window.eventBus.$on('403', this.accessDenied);
    window.eventBus.$on('contact-deleted', this.removeContact);
    this.finalizeSplashView();
  },
  methods: {
    ...mapActions({
      fetchBlockedContacts: 'merchant/fetchBlockedContacts',
      finalizeSplashView: 'organisation/finalizeSplashView',
      fetchContact: 'contact/fetchContact',
      blockContact: 'contact/blockContact',
    }),
    ...mapMutations({
      CLEAR_BLOCKED_CONTACTS: 'merchant/CLEAR_BLOCKED_CONTACTS',
      ADD_BLOCKER_CONTACT_TO_LIST: 'merchant/ADD_BLOCKER_CONTACT_TO_LIST',
      REMOVE_BLOCKER_CONTACT_FROM_LIST: 'merchant/REMOVE_BLOCKER_CONTACT_FROM_LIST',
    }),
    escapeStringRegexp,
    removeHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        cell.classList.remove('hover-grid');
        cell.classList.remove('last');
      }
    },
    addHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        if (index === 0) {
          cell.classList.add('first');
        }
        cell.classList.add('hover-grid');
        if (index === cells.length - 1) {
          cell.classList.add('last');
        }
      }
    },
    nextPage() {
      if (!this.blocklist || !this.blocklist.meta || this.blocklist.meta.current_page >= this.blocklist.meta.last_page) {
        return;
      }
      this.loadPage(this.blocklist.meta.current_page + 1);
    },
    loadPage(page, append = true) {
      this.fetchBlockedContacts({
        businessId: this.business.id,
        data: {
          page,
          limit: this.pageLimit,
          search_query: this.searchQuery,
        },
        append,
      });
    },
    loadMoreIfNeeded() {
      if (!this.$refs.blocklistScrollRef.verticalScrollActive) {
        this.nextPage();
      }
    },
    selectRow() {

    },
    accessDenied() {
      this.forbidden = true;
    },
    unblock(contact) {
      let message;
      if (contact.name) {
        message = `Are you sure you want to unblock contact <b>${contact.name}</b> ${this.$options.filters.phoneFormat(contact.phone)}?`;
      } else {
        message = `Are you sure you want to unblock contact <b>${this.$options.filters.phoneFormat(contact.phone)}</b>?`;
      }
      // CS - Use filter from code
      this.$confirmPluginModal.show({
        title: 'Unblock contact',
        message,
        confirmButtonText: 'Unblock',
        autoClose: 'true',
        confirmCallback: () => {
          this.blockContact({
            businessId: this.business.id,
            conversationId: contact.conversation_id,
            data: {
              block: '0',
            },
          })
            .then((response) => {
              if (response.status === 200) {
                this.REMOVE_BLOCKER_CONTACT_FROM_LIST(contact.id);
                this.showToaster(`${contact.name ? contact.name : contact.phone} has been successfully unblocked.`, null, null, 'OK');
                this.$confirmPluginModal.close();
              }
            })
            .catch(() => {
              this.showToaster(`There was an error while unblocking ${contact.name ? contact.name : contact.phone}. Try again later.`, null, null, 'warning');
            });
        },
      });
    },
    removeContact() {

    },
    searchDebounce: debounce(function f() {
      this.searchModeActive = true;
      this.loadPage(1, false);
    }, '0'),
  },
};
</script>

<style lang="scss" scoped>
.spinner-border-top {
  border-top: 1px solid #e6ebed;
  margin-top: 0 !important;
  padding-top: 40px;
}

.actions-bar {
  margin-top: 35px;
  margin-bottom: 15px;
}

.table__grid {
  grid-template-columns: minmax(25%, min-content) minmax(50px, auto) max-content !important; // minmax(auto, 50%) minmax(0, min-content)

  .full-width {
    grid-column: 1 / 4 !important;
  }

  .grid-item {
    padding-top: 13px;
    padding-bottom: 13px;

    &.gi-button {
      padding-top: 4px;
      padding-bottom: 4px;
      justify-content: end;
    }

    .btn {
      visibility: hidden;
      padding-top: 4px;
      padding-bottom: 4px;

      span {
        line-height: 1;
        font-size: 12px;
        font-size: 13px;
        height: 16px;
        font-weight: 500;
      }
    }
  }

  .hover-grid {
    &.grid-item {
      cursor: default;
      .btn {
        visibility: visible;
      }
    }
  }
}

.branch-icon {
  margin-right: 11px;
}

::v-deep .copy-text {
  a.dropdown-item {
    padding: 0 !important;

    span {
      display: block;
      padding: 10px 15px;
    }
  }
}

.hide.grid-item {
  display: none !important;
}

.no-table-data {
  color: $greyPrimary;
}

::v-deep {
  .sb-avatar {
    margin-right: 9px;

    .logo.s-button {
      width: 26px !important;
      height: 26px !important;
    }
  }
}

.scroll-null {
  margin: -35px 0 -35px 0;
  height: calc(100% + 35px);

  .settings__title {
    padding-top: 70px;
    margin-top: 0;
  }
}
</style>
